import React, { useEffect, useState, useRef, useCallback } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useIntl } from "react-intl";
import moment from "moment";

import {
  Box,
  Container,
  Icon,
  IconButton,
  InputAdornment,
  OutlinedInput,
} from "@mui/material";

//Components
import { SliderRangePanel } from "Modules/.common/components/SliderRangePanel";
import TableView, { ToobarButton, ExportButton } from "Components/TableView";
// import { TableView, ToobarButton, ExportButton } from '@gpstrackit/gtc-react-controls'

import { NotificationManager } from "Components/Notifications";
import Modal, { ModalBody } from "Components/Modal";
import useExport from "Components/Table/Export";
import { PillTime } from "../tools/PillTime";
import TabInfo from "../tools/TabInfo";

import VideoAssignDriver from "Modules/.common/components/VideoAssignDriver";
import DeleteVideoModal from "../../../../modals/delete";
import ChangeStatusModal from "../../../../modals/changeStatus";
import AdvancedFilters from "Modules/video/views/main/.components/advancedFilters";
import Preferences from "Core/containers/Preferences";

//Views
import EventCard from "./.components/eventCard";
import AddNote from "./.components/addNote";

//Helpers
import { getLabel } from "Modules/video/.globals/helpers";
import { HasPermission } from "Core/security";

import {
  GetFormatForMoment,
  getUserFormattedDate,
  getFormattedDateByTimeZone,
  speedFormatMPH,
  getInitialRange
} from "Core/data/Helpers";

//Actions
import {
  clearRedux,
  openDeleteMediaDetailModal,
  openEventAddNotesModal,
  openSharedMediaModal,
  sendGenericExport,
  clipRequestGetUnits,
  setRequestClipData,
  clipRequestOpenForm,
  searchAvailableClips,
  resetCatalog,
  openModalDeleteVideo,
  savePreference,
  restoreAvailableMedia
} from "Redux/actions";

//Styles
import "../../styles.scss";
import { openModalChangeStatus } from "../../../../modals/changeStatus/redux/slice";
import { searchVideoEvents, reset, setFilters, query, deleteEvent, updateEventsData } from "./redux/slice";
import { useEventsSearch, EVENTS_LIST_QUERY_KEY } from "./useEventsSearch";

export { EVENTS_LIST_QUERY_KEY };

const limit = 20;

export const EventsTab = ({ index, tableViewRef, tabName }) => {
  // Hooks
  const dispatch = useDispatch();
  const { messages, locale } = useIntl();

  const {
    data: reduxData,
    loading: reduxLoading,
    filters: reduxFilters,
    error,
    nextToken,
    updateRenderData,
  } = useSelector((state) => state.videoEventsListRedux);

  const loadingExport = useSelector((state) => state.commomRedux.loadingExport);
  const videoChangeStatus = useSelector(
    (state) => state.catalogsNRedux.videoChangeStatus
  );
  const videoDetailUpdated = useSelector(
    (state) => state.catalogsRedux.videoDetailUpdated
  );
  const videoDetailUpdatedNotes = useSelector(
    (state) => state.catalogsRedux.videoDetailUpdatedNotes
  );
  const { visibleForm, loadingCreateClip } = useSelector(
    (state) => state.clipsRedux
  );

  const user = useSelector((state) => state.securityRedux.user);
  const authenticatedId = useSelector((state) => state.securityRedux.authenticatedId);
  const urlLightMetricsUpdated = useSelector(
    (state) => state.catalogsRedux.urlLightMetricsUpdated
  );
  const { isKph, dateformat, timeZone } = user;

  //Local States
  const [selectedRows, setSelectedRows] = useState([]);
  const [searchText, setSearchText] = useState("");
  const [typing, setTyping] = useState(false);
  const [filterOpen, setFilterOpen] = useState(false);
  const isStarredTab = tabName === "videoStarred";
  const isDeletedTab = tabName === "videoDeleted";
  const isEventsTab = tabName === "videoEvents";
  const [videoEvents, setVideoEvents] = useState([]);

  const eventsSearch = useEventsSearch({ tabName, enabled: isEventsTab, tableViewRef });

  const data = isEventsTab ? eventsSearch.data : reduxData;
  const loading = isEventsTab ? eventsSearch.loading : reduxLoading;
  const filters = isEventsTab ? eventsSearch.filters : reduxFilters;

  //Variables
  const format = GetFormatForMoment(dateformat);
  const defaultFrame = "today";

  useEffect(() => {
    setVideoEvents([...data]);

    if (updateRenderData)
      dispatch(reset({ updateRenderData: false }));

  }, [data, updateRenderData]);

  useEffect(() => {
    if (urlLightMetricsUpdated?.length > 0) {
      // P3-53907: dispatch through updateEventsData (same action deleteEvent/etc.
      // already use) instead of resetting `data` directly — this tab reads from
      // TanStack Query (see useEventsSearch.js) once migrated, and only
      // updateEventsData bumps `lastPatch`, which is the bridge that keeps the
      // TanStack Query cache in sync. Writing `data` directly here was a no-op
      // for this tab, so the refreshed URL never showed up without a full reload.
      const patches = [];
      data.forEach((event) => {
        const findEvent = urlLightMetricsUpdated.find(
          (item) => item.id == event.id
        );
        if (findEvent) {
          const deviceData = JSON.parse(event.deviceData);
          const deviceDataUpdated = {
            ...deviceData,
            url: findEvent.url,
            urlExpiration: findEvent.urlExpiration,
          };
          patches.push({
            id: event.id,
            deviceData: JSON.stringify(deviceDataUpdated),
            url: findEvent.url,
          });
        }
      });
      if (patches.length > 0) {
        dispatch(updateEventsData({ events: patches }));
      }
      dispatch(clearRedux("CATALOG_REDUX", { urlLightMetricsUpdated: null }));
    }
  }, [urlLightMetricsUpdated]);

  useEffect(() => {
    if (loading || index === 1 || index === 4) return;

    return () => {
      dispatch(reset());
    };
  }, []);

  useEffect(() => {
    if (error) {
      dispatch(reset({ error: null }));

      NotificationManager.error(
        messages["errorMessage"],
        messages["error"],
        3000,
        null,
        null,
        "warning"
      );
    }
  }, [error]);

  useEffect(() => {
    if (
      videoChangeStatus &&
      (videoChangeStatus?.rowsId?.length || videoChangeStatus?.ids?.length) &&
      index === 0
    ) {
      let currentEvents = [...videoEvents];
      let currentTotal = total;
      let rowsId = [];
      if (videoChangeStatus?.ids?.length) {
        rowsId = videoChangeStatus?.ids.map((item) => item?.rowId);
      }
      if (videoChangeStatus.rowsId) {
        rowsId = videoChangeStatus.rowsId;
      }

      rowsId.forEach((id) => {
        const eventIndex = currentEvents.findIndex((event) => event.id === id);
        if (eventIndex !== -1) {
          currentEvents[eventIndex] = {
            ...currentEvents[eventIndex],
            notes:
              currentEvents[eventIndex]?.id == videoDetailUpdated &&
                videoDetailUpdatedNotes
                ? videoDetailUpdatedNotes
                : currentEvents[eventIndex]?.notes,
            videoReview: {
              ...currentEvents[eventIndex].videoReview,
              status: videoChangeStatus.reviewStatus,
            },
          };
        }
      });
      dispatch(
        clearRedux("AVAILABLE_MEDIA", {
          events: currentEvents,
          total: currentTotal,
        })
      );
      dispatch(
        clearRedux("CATALOG_REDUX", {
          videoDetailUpdated: null,
          videoDetailUpdatedNotes: null,
        })
      );
      dispatch(resetCatalog({ videoChangeStatus: null }));
      dispatch(reset({ updateRenderData: false }));
    }
  }, [videoChangeStatus, videoDetailUpdated]);

  const handleChangePage = useCallback((newPage, newRowsPerPage) => {
    if (isEventsTab) {
      eventsSearch.handleChangePage();
      return;
    }

    const realPage = newPage - 1;
    let newAllFilters = {
      ...filters,
      timeFrame: filters?.timeFrame,
      resetSearch: true,
      limit: limit,
      nextToken: nextToken,
      dateRange: filters?.dateRange,
      starred: isStarredTab,
      mediaDeleted: isDeletedTab,
    };

    dispatch(searchVideoEvents({ args: newAllFilters }));
  });

  useEffect(() => {
    if (typing) {
      const timer = setTimeout(() => {
        handleSearch(searchText);
      }, 1000);
      return () => clearTimeout(timer);
    }
  }, [searchText]);

  const handleSearch = useCallback((searchText) => {
    if (isEventsTab) {
      eventsSearch.handleSearch(searchText);
      return;
    }

    let newAllFilters = {
      ...filters,
      openSearch: searchText,
      nextToken,
      starred: isStarredTab,
      mediaDeleted: isDeletedTab,
    };

    tableViewRef.current?.setSelectedRows([])

    dispatch(
      savePreference({
        entityName: "User",
        entityId: authenticatedId,
        preferenceKey: tabName,
        preferenceValue: { filters: newAllFilters },
      })
    );

    tableViewRef?.current?.setNewPage(1);
    dispatch(searchVideoEvents({ args: { ...newAllFilters, nextToken: null } }));
  });


  const getHeaders = () => {
    return [
      messages["videoMainView_unit"],
      messages["videoMainView_driver"],
      messages["videoMainView_event"],
      messages["videoMainView_address"],
      messages["videoMainView_dateTime"],
      messages["videoMainView_speed"],
      messages["videoMainView_notes"],
    ];
  };

  const getDataToExport = (rows) => {
    let data = [];

    rows.forEach((row) => {
      const unitlabel = row?.label || "";
      const driverName = row?.driverName || "";
      const eventName = row?.eventName || "";
      const formattedAddress = row?.formattedAddress || "";
      const unitTime = getFormattedDateByTimeZone(
        row?.unitTime,
        row?.timeZone?.name,
        messages,
        "llll"
      );
      const newSpeed = row?.useEcuSpeed ? row?.ecuSpeed : row?.speed;
      const speed = speedFormatMPH(newSpeed, row?.unitType, isKph);
      const notes = row?.notes;

      data.push([
        unitlabel,
        driverName,
        eventName,
        formattedAddress,
        unitTime,
        speed,
        notes,
      ]);
    });

    return data;
  };

  const exportCSVPdf = useExport(
    getHeaders,
    getDataToExport,
    [],
    selectedRows || [],
    null,
    `mediaEvents-${moment().unix()}`,
    "landscape",
    true
  );

  const getSpeed = () => {
    return `let newSpeed = row?.unit?.useEcuSpeed ? row?.reading?.ecuSpeed : row?.reading?.speed; if (!newSpeed) return ""; ${isKph
      ? ` let kph = 0; if (parseInt(newSpeed) > 0) { kph = (newSpeed * 1.609344); } return Math.ceil(kph) + "KPH";`
      : ` let speed = Math.round(newSpeed); return speed + " MPH" `
      }`;
  };

  const getFormatedRange = (initialTime) => {
    const formatedRange = {
      start: moment(initialTime).subtract(1, "s").format(),
      end: moment(initialTime).add(5, "m").format(),
    };

    return formatedRange;
  };


  const openSliderVideo = (rowSelected) => {
    const {
      unitTime,
      label,
      deviceId,
      deviceId: gpsId,
      timeZone,
    } = rowSelected;

    const utcTime = new Date(unitTime).toLocaleString("en", {
      timeZone: timeZone?.name,
    });

    const dateRange = getFormatedRange(utcTime);
    const dateTime = new Date(moment(utcTime).subtract(1, "s").format());

    const dataTimeLine = {
      deviceId: deviceId,
      label,
      dateRange,
      dateTime: dateTime,
      gpsId,
      hideNotification: true,
      noSearchAvClips: true,
    };
    dispatch(searchAvailableClips({ esn: deviceId }));
    dispatch(setRequestClipData(dataTimeLine));
    setTimeout(() => {
      dispatch(clipRequestOpenForm(true));
    }, 1000);
  };

  return (
    <>
      <Preferences
        preferencesKey={tabName}
        onInitialPreferences={(userPreferences) => {
          if (isEventsTab) {
            eventsSearch.handleInitialPreferences(userPreferences);
            return;
          }

          let newFilters = {};
          if (!userPreferences?.filters) {
            //without preferences
            newFilters = {
              ...filters,
              dateRange: getInitialRange(filters?.timeFrame || defaultFrame, filters.dateRange?.start, filters.dateRange?.end)
            };

            dispatch(
              savePreference({
                entityName: "User",
                entityId: authenticatedId,
                preferenceKey: tabName,
                preferenceValue: { ...userPreferences, filters: filters },
              })
            );
          } else {
            //with save preferences

            newFilters = userPreferences?.filters;

            newFilters = {
              ...newFilters,
              dateRange: getInitialRange(newFilters?.timeFrame || defaultFrame, newFilters.dateRange?.start, newFilters.dateRange?.end)
            };

            if (newFilters?.openSearch != searchText)
              setSearchText(newFilters?.openSearch || "")
          }

          if (tabName === "videoStarred") {
            newFilters.starred = true;
          }

          if (tabName === "videoDeleted") {
            newFilters.mediaDeleted = true;
          }

          dispatch(setFilters(newFilters));
        }}
      >
        <AddNote />
        <VideoAssignDriver refresh={() => handleSearch(searchText)} />

        <Container
          maxWidth={false}
          style={{ height: "100%", paddingBottom: 24, paddingLeft: 12 }}
        >
          <Box
            width="100%"
            display="flex"
            mb="30px"
            alignItems={"center"}
            justifyContent="space-between"
          >
            <Box style={{ width: "20%" }}>
              <ToobarButton
                icon="filter_list"
                tooltip={messages["table_filter"]}
                className={
                  filters ? "select-view-icon-filter" : "select-view-icon"
                }
                onClick={() => {
                  setFilterOpen(!filterOpen);
                  dispatch(openSharedMediaModal({ open: false }));
                  dispatch(openEventAddNotesModal({ open: false }));
                }}
              />
              {
                <ExportButton
                  className="media-events-icon-download"
                  style={{ color: "black" }}
                  onExport={(type) => {
                    if (selectedRows?.length) {
                      if (type === "CSV") {
                        exportCSVPdf.exportCSV();
                      } else if (type === "PDF") {
                        exportCSVPdf.exportPDF();
                      }
                      NotificationManager.success(
                        messages["exportSuccess"],
                        messages["success"],
                        4000,
                        null,
                        null,
                        "success",
                        null
                      );
                    } else {
                      const variables = {
                        limit: filters.limit,
                        dateRange: filters.dateRange,
                        openSearch: filters.openSearch,
                        starred: filters.starred,
                        entities: filters.entities || [{ name: "All", id: -1 }],
                        hideTableLoading: filters.hideTableLoading,
                        eventNames: filters.events,
                        mediaDeleted: filters.mediaDeleted,
                        mediaType: filters.mediaType,
                        reviewStatus: filters.reviewStatus,
                        hasNotes: filters.hasNotes,
                      };

                      const exportColumns = [
                        {
                          header: messages["videoMainView_unit"],
                          column: "unitLabel",
                          functionString: 'return row?.unit?.label || ""',
                        },
                        {
                          header: messages["videoMainView_driver"],
                          column: "driver",
                          functionString:
                            'return row?.reading?.driverName || ""',
                        },
                        {
                          header: messages["videoMainView_event"],
                          column: "event",
                          functionString:
                            'return row?.reading?.eventName || ""',
                        },
                        {
                          header: messages["videoMainView_address"],
                          column: "address",
                          functionString:
                            'return row?.reading?.formattedAddress || ""',
                        },
                        {
                          header: messages["videoMainView_dateTime"],
                          column: "dateTime",
                          functionString:
                            'return moment(row?.reading?.unitTime).tz(row?.unit?.timeZone?.name).format("llll") || ""',
                        },
                        {
                          header: messages["videoMainView_speed"],
                          column: "speed",
                          functionString: getSpeed(),
                        },
                        {
                          header: messages["videoMainView_notes"],
                          column: "notes",
                          functionString: 'return row?.reading?.notes || ""',
                        },
                      ];

                      const getEntities = filters?.entities?.map((entity) => entity?.name).join(" / ") || "All";
                      const getEntitiesName = filters?.entities?.map((entity) => entity?.label).join(" / ") || "";

                      const tableHeader = [
                        {
                          name: messages["videoMainView_videoEvents"],
                          value: "",
                        },
                        {
                          name: messages["videoMainView_filters"],
                          value: getEntities
                        },
                        {
                          name: messages["videoMainView_units"],
                          value: getEntitiesName,
                        },
                        {
                          name: messages["videoMainView_events"],
                          value: filters?.events?.join(", "),
                        },
                        {
                          name: messages["videoMainView_openSearch"],
                          value: filters?.openSearch,
                        },
                        {
                          name: messages["videoMainView_dateRange"],
                          value: `${getUserFormattedDate(
                            user,
                            variables?.dateRange?.start,
                            messages,
                            "h:mm A",
                            false,
                            true
                          )} - ${getUserFormattedDate(
                            user,
                            variables?.dateRange?.end,
                            messages,
                            "h:mm A",
                            false,
                            true
                          )}`,
                        },
                      ];

                      if (variables?.hasNotes) {
                        tableHeader.push({
                          name: messages["videoMainView_hasNotes"],
                          value: "",
                        });
                      }

                      if (variables.starred) {
                        tableHeader.push({
                          name: `${messages["videoMainView_starred"]}:`,
                          value: "True",
                        });
                      }

                      if (variables.mediaDeleted) {
                        tableHeader.push({
                          name: `${messages["videoMainView_deleted"]}:`,
                          value: "True",
                        });
                      }

                      dispatch(
                        sendGenericExport(
                          "mediaEvents",
                          type === "CSV" ? "Csv" : "Pdf",
                          query,
                          variables,
                          exportColumns,
                          {
                            unit: "pt",
                            size: "A4",
                            orientation: "landscape",
                            fontSize: 10,
                          },
                          tableHeader,
                          true,
                          true,
                          {
                            1: { cellWidth: 140 },
                            6: { cellWidth: 140 },
                          }
                        )
                      );
                    }
                  }}
                />
              }

              {
                selectedRows.length !== 0 && (
                  <>
                    <ToobarButton
                      icon="share"
                      tooltip={messages["videoMainView_share"]}
                      disabled={selectedRows.length === 0}
                      onClick={() => {
                        let items = [];
                        selectedRows.forEach((selectedRow) => {
                          const { id, deviceId } = selectedRow;
                          items.push({
                            entity: {
                              id,
                              name: "DeviceReading",
                              label: getLabel(messages, selectedRow, locale),
                            },
                            deviceId,
                          });
                        });
                        setFilterOpen(false);
                        dispatch(
                          openSharedMediaModal({
                            open: true,
                            items: items,
                          })
                        );
                      }}
                    />

                    {
                      filters?.category !== "deleted" &&
                      HasPermission(user, ["dtlvdo"], [282]) &&
                      index !== 3 && (
                        <ToobarButton
                          icon="delete"
                          tooltip={messages["table_delete"]}
                          disabled={selectedRows.length === 0}
                          onClick={() => {
                            let items = [];
                            selectedRows?.forEach((selectedRow) => {
                              const { id, deviceId, videoReview } = selectedRow;
                              items.push({
                                id,
                                label: getLabel(messages, selectedRow, locale),
                                deviceId,
                                status: videoReview?.status || "Unreviewed",
                              });
                            });

                            dispatch(
                              openModalDeleteVideo({
                                type: "media",
                                items: items.map((x) => {
                                  return {
                                    ...x,
                                    id: { rowId: x.id, deviceId: x.deviceId },
                                  };
                                }),
                              })
                            );
                          }}
                        />
                      )}

                    {
                      tabName === "videoDeleted" && (
                        <ToobarButton
                          icon="restore_from_trash"
                          tooltip={messages["videoMainView_restore"]}
                          disabled={selectedRows.length === 0}
                          onClick={
                            () => {
                              dispatch(restoreAvailableMedia(selectedRows));
                              dispatch(deleteEvent({ eventIds: selectedRows.map((x) => x.id) }));
                              setSelectedRows([]);
                              tableViewRef?.current?.setSelectedRows([]);
                              NotificationManager.success(
                                messages["videoMainView_restoreSuccess"],
                                messages["success"],
                                3000,
                                null,
                                null,
                                "success"
                              );
                            }
                          }
                        />
                      )}

                    <ToobarButton
                      icon="settings"
                      tooltip={messages["videoMainView_changeStatus"]}
                      disabled={selectedRows.length === 0}
                      onClick={() =>
                        dispatch(
                          openModalChangeStatus({
                            items: selectedRows.map((x) => {
                              return { ...x, rowId: x.id, deviceId: x.deviceId };
                            }),
                          })
                        )
                      }
                    />
                  </>
                )}
            </Box>
            <Box
              style={{
                display: "flex",
                width: "100%",
                justifyContent: "space-between",
              }}
            >
              <Box
                style={{
                  width: "60%",
                  display: "flex",
                  justifyContent: "center",
                  alignItems: "center",
                }}
              >
                <PillTime
                  dateRange={filters?.dateRange}
                  timeFrame={filters?.timeFrame || defaultFrame}
                  timezone={timeZone}
                  format={format}
                  onClick={() => {
                    setFilterOpen(true);
                    // dispatch(openSharedMediaModal({ open: false }));
                    // dispatch(openEventAddNotesModal({ open: false }));
                    // dispatch(openDeleteMediaDetailModal({ open: false }));
                  }}
                />
              </Box>
              <Box style={{ width: "40%" }}>
                <OutlinedInput
                  disabled={loading}
                  id={"custom-search"}
                  placeholder={messages["videoMainView_search"]}
                  type={"text"}
                  value={searchText || ""}
                  fullWidth={true}
                  onChange={(e) => {
                    setSearchText(e.target.value);
                    // handleSearch(e.target.value);
                    setTyping(true);
                  }}
                  endAdornment={
                    <InputAdornment position="end">
                      <IconButton
                        aria-label="placeholder"
                        style={{ fontSize: 25, color: "black" }}
                      >
                        <Icon>search</Icon>
                      </IconButton>
                    </InputAdornment>
                  }
                />
              </Box>
            </Box>
          </Box>
          <Box style={{ margin: "-20px 0 20px 0" }}>
            <TabInfo />
          </Box>
          <TableView
            ref={tableViewRef}
            infiniteScroll={true}
            serverSide={true}
            moreRowsAvailable={true}
            data={videoEvents || []}
            loading={
              loading ||
              loadingExport ||
              urlLightMetricsUpdated?.id
            }
            rowsPerPage={10}
            name="Table View"
            topSpaceSize={330}
            viewType="card"
            sizeInput={3}
            messages={messages}
            hideAllInPagination
            inputSearch={false}
            renderRow={(row) => {
              return (
                <div key={row?.id}>
                  <EventCard
                    row={row}
                    tab={index}
                    messages={messages}
                    isDeleted={tabName === "videoDeleted"}
                    openSliderVideo={(rowSelected) =>
                      openSliderVideo(rowSelected)
                    }
                    callback={(event) => {
                      dispatch(deleteEvent({ eventIds: [event.id] }))
                    }}
                  />
                </div>
              )
            }}
            // total={total}
            onChangePage={(page) => {
              handleChangePage(page);
            }}
            onSearch={(searchText) => {
              handleSearch(searchText);
            }}
            toolbarContainerClassName="table-toolbar-container"
            setSelectedRows={(rows) => {
              setSelectedRows(rows);
            }}
            showCheck={[0, 3].includes(index) ? true : false}
          >

          </TableView>
          <Modal
            open={visibleForm}
            loading={loadingCreateClip}
            className="slider-clip"
            size="large"
          >
            <ModalBody className="clip-request-modal-body">
              <SliderRangePanel />
            </ModalBody>
          </Modal>
          <DeleteVideoModal
            onClose={(refresh, type) => {
              setSelectedRows([]);
              tableViewRef?.current?.setSelectedRows([]);
              if (refresh || type === "media") {
                tableViewRef?.current?.setNewPage(1);
                handleChangePage(1);
              }
            }}
          />

          <ChangeStatusModal onClose={(result) => {
            if (result) {
              tableViewRef.current?.setSelectedRows([])
            }
          }} />

        </Container>

        <AdvancedFilters
          open={filterOpen}
          currentFilters={filters}
          setOpen={setFilterOpen}
          indexTab={0}
          onChangeFilters={(newAllFilters) => {
            if (isEventsTab) {
              eventsSearch.handleFiltersChange(newAllFilters);
              setFilterOpen(false);
              return;
            }

            dispatch(
              savePreference({
                entityName: "User",
                entityId: authenticatedId,
                preferenceKey: tabName,
                preferenceValue: { filters: newAllFilters },
              })
            );
            tableViewRef?.current?.setNewPage(1);
            dispatch(
              searchVideoEvents({ args: { ...newAllFilters, nextToken: null } })
            );
            setFilterOpen(false);
          }}
        />
      </Preferences >
    </>
  );
};

export default EventsTab;
